export function clampSeekTime(
    seconds: number,
    audio: HTMLMediaElement,
    durationHint?: number | null,
): number | null {
    const hint = durationHint ?? null;
    const mediaDur =
        Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : null;
    const max = mediaDur ?? hint;
    if (max == null || max <= 0) return null;
    return Math.max(0, Math.min(seconds, max));
}

export function getMediaBufferRatio(media: HTMLMediaElement): number {
    const { duration, buffered } = media;
    if (!Number.isFinite(duration) || duration <= 0 || buffered.length === 0) {
        return 0;
    }
    let end = 0;
    for (let i = 0; i < buffered.length; i++) {
        end = Math.max(end, buffered.end(i));
    }
    return Math.min(1, end / duration);
}

export function getBufferDisplayPercent(
    media: HTMLMediaElement,
    downloadProgressPercent: number,
): number {
    const mediaPercent = getMediaBufferRatio(media) * 100;
    return Math.max(mediaPercent, downloadProgressPercent);
}

export async function fetchAudioBlobWithProgress(
    url: string,
    signal: AbortSignal,
    credentials: RequestCredentials,
    onProgress: (percent: number) => void,
): Promise<Blob> {
    const res = await fetch(url, { credentials, signal });
    if (!res.ok) {
        throw new Error(`Audio fetch failed: ${res.status}`);
    }

    const total = Number(res.headers.get("content-length")) || 0;
    if (!res.body) {
        const blob = await res.blob();
        onProgress(100);
        return blob;
    }

    const reader = res.body.getReader();
    const chunks: BlobPart[] = [];
    let received = 0;

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        if (total > 0) {
            onProgress(Math.min(100, (received / total) * 100));
        }
    }

    onProgress(100);
    return new Blob(chunks);
}
